/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./AddCard.css";
import { Link } from "react-router-dom";
import {
  CardElement,
} from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import errorMsg from "../../assets/json/errMsg.json";
import { simpleFetch } from "../../restWrapper";
import { Helmet } from "react-helmet";

class AddCard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ...this.props.location.state,
      ...{
        language: "zh",
        payerInfo: {
          payerName: "",
          payerEmail: "",
          payerPhone: "",
        },
      },
      err: "",
      loading: false,
    };

    this.state.payerInfo.payerName =
      this.state.donorInfo.firstName + " " + this.state.donorInfo.lastName;
    this.state.payerInfo.payerEmail = this.state.donorInfo.email;
    this.state.payerInfo.payerPhone = this.state.donorInfo.phoneNumber;

    this.handlePayerName = this.setPayerInfoData.bind(this, "payerName");
    this.handlePayerEmail = this.setPayerInfoData.bind(this, "payerEmail");
    this.handlePayerPhone = this.setPayerInfoData.bind(this, "payerPhone");

    this._onClickBtn = () => {
      if (this.state.loading) return null;

      if (!this.props.stripe) {
        const err = errorMsg[this.state.language].fail;
        this.setState({
          err: err,
        });
        return null;
      }

      this.setState({ loading: true, err: "" });

      // Use Elements to get a reference to the Card Element mounted somewhere
      // in your <Elements> tree. Elements will know how to find your Card Element
      // because only one is allowed.
      // See our getElement documentation for more:
      // https://stripe.com/docs/stripe-js/reference#elements-get-element

      const cardElement = this.props.elements.getElement("card");
      // console.log(cardElement);

      // From here we can call createPaymentMethod to create a PaymentMethod
      // See our createPaymentMethod documentation for more:
      // https://stripe.com/docs/stripe-js/reference#stripe-create-payment-method

      this.props.stripe
        .createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: this.state.payerInfo.payerName,
            email: this.state.payerInfo.payerEmail,
            phone: this.state.payerInfo.payerPhone,
          },
        })
        .then(({ paymentMethod, error }) => {
          let err = errorMsg[this.props.language].paymentErr.cardInfo;
          // console.log("Received Stripe PaymentMethod:", paymentMethod);

          if (!paymentMethod) {
            this.setState({
              loading: false,
              err: (error && error.message) || err,
            });
            return null;
          }

          return simpleFetch("/cards", "POST", {
            paymentMethod: paymentMethod.id,
          }).then((res) => {
            // console.log(res);
            let dataObj = this.state;
            if (res.status === 412 && res.status !== 200) {
              const err =
                errorMsg[this.props.language].paymentErr.dupCard;
              this.setState({
                loading: false,
                err: err,
              });
              return null;
            }
            if (res.status !== 200 && res.status !== 412) {
              const err =
                errorMsg[this.props.language].paymentErr.cardInfo;
              this.setState({
                loading: false,
                err: err,
              });
              return null;
            }
            // console.log(res.body.status + "101");

            if (res.body.status === "requires_action") {
              // Hide full-page loader so the 3DS iframe can be interacted with
              this.setState({
                loading: false,
                stripe3DS: res.body.stripe3DS,
                status: res.body.status,
                setupIntent: res.body.setupIntent,
              });

              // Consecutive failed status checks tolerated (~30s) before
              // giving up: the SetupIntent may still succeed, and a retry
              // could attach the card twice
              const MAX_FAILED_POLLS = 15;
              let failedPolls = 0;

              /* fetch Stripe payment status every two seconds */
              this.retrieveCardStatus = setInterval(() => {
                simpleFetch("/cards/retrieveCardStatus", "POST", {
                  setupIntent: this.state.setupIntent,
                }).then(
                  (res) => {
                    if (res.status !== 200) throw new Error("Card status check failed");
                    return res;
                  }
                ).then((res) => {
                  failedPolls = 0;
                  this.setState({
                    status: res.body.status,
                  });
                  // console.log(res.body.status + "119");
                  if (res.body.status === "succeeded") {
                    clearInterval(this.retrieveCardStatus);
                    this.setState({ loading: true });

                    this.props.history.push({
                      pathname: "/ListCards",
                      state: dataObj,
                    });
                  } else if (
                    res.body.status === "requires_payment_method"
                  ) {
                    clearInterval(this.retrieveCardStatus);
                    this.setState({
                      loading: false,
                      err: errorMsg[this.props.language].paymentErr.cardInfo,
                    });
                  }
                }, () => {
                  // Only failures of the status check land here, not throws
                  // from the handler above. Ride out a blip; give up after a
                  // run of them so the overlay and frozen page don't trap
                  // the donor
                  failedPolls += 1;
                  if (failedPolls < MAX_FAILED_POLLS) return;
                  clearInterval(this.retrieveCardStatus);
                  this.setState({
                    loading: false,
                    status: "",
                    err: errorMsg[this.props.language].paymentErr.cardInfo,
                  });
                });
              }, 2000);
            }

            if (res.body.status === "succeeded") {
              dataObj.paymentMethodId = paymentMethod.id;
              dataObj.brand = paymentMethod.card.brand;
              dataObj.last4 = paymentMethod.card.last4;
              // console.log(paymentMethod);
              // Keep loading true until navigation completes
              this.props.history.push({
                pathname: "/ListCards",
                state: dataObj,
              });
            }
          });
        })
        .catch(() => {
          this.setState({
            loading: false,
            err: errorMsg[this.props.language].paymentErr.cardInfo,
          });
        });
    };
  }

  componentDidMount() {
    if (!this.props.isAuth) {
      this.props.history.push("/SelectMission");
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Freeze the page behind the 3DS overlay; otherwise it still scrolls
    // and shows through the backdrop
    const is3DS = this.state.status === "requires_action";
    if (is3DS !== (prevState.status === "requires_action")) {
      document.body.style.overflow = is3DS ? "hidden" : "";
    }
  }

  componentWillUnmount() {
    if (this.retrieveCardStatus) {
      clearInterval(this.retrieveCardStatus);
    }
    document.body.style.overflow = "";
  }

  setPayerInfoData(name, e) {
    let data = this.state.payerInfo;
    data[name] = e.target.value;
    let err = this.state.errorMsg;
    this.setState({
      errorMsg: err,
      payerInfo: data,
    });
  }

  render3DS() {
    if (this.state.status === "requires_action") {
      return (
        <div className="AddCard-iframeContainer">
          <iframe
            className="AddCard-iframe"
            src={this.state.stripe3DS}
            title="3D Secure"
            width="100%"
            height="100%"
          />
        </div>
      );
    } else return <div></div>;
  }

  render() {
    if (this.state.loading) {
      return <this.props.RenderFullPageLoading />;
    }
    const languageObj = this.props.languageObj;

    const color = this.props.location.state.color;

    const buttonOverrideStyle = {
      background: "#" + this.props.location.state.color.headerColor,
      color: "#" + this.props.location.state.color.buttonColor,
    };

    const organizationName = this.props.location.state.organizationName;

    if (this.state.donor) {
      // console.log(this.state);
      // console.log(this.state.donor.firstName);
    }

    return (
      <div className="AddCard-view">
        {this.render3DS()}
        <Helmet>
          <title>{organizationName}</title>
        </Helmet>
        <div className="AddCard-bg" style={buttonOverrideStyle} />
        <div className="AddCard-main">
          <div className="AddCard-ccContainer">
            <Link
              to={{
                pathname: "/ListCards",
                state: {
                  color: color,
                  organizationName: organizationName,
                },
              }}
            >
              <div className="AddCard-backContainer">
                <div className="AddCard-back">
                  <div className="AddCard-backBtn"></div>
                  <div className="AddCard-backText">{languageObj.backBtn}</div>
                </div>
              </div>
            </Link>

            <div>
              <div className="AddCard-paymentNetworks" />
              <div className="AddCard-subheading">
                {languageObj.subheading[0]}
              </div>
              <div className="AddCard-inputFieldContainer">
                <input
                  className="AddCard-inputField"
                  value={this.state.payerInfo.payerName}
                  onChange={this.handlePayerName}
                  style={{ base: { fontSize: "18px" } }}
                />
              </div>

              <div className="AddCard-inputFieldGroup">
                <div className="AddCard-subheading">
                  {languageObj.subheading[1]}
                </div>

                <div className="AddCard-inputFieldContainer">
                  <input
                    className="AddCard-inputField"
                    value={this.state.payerInfo.payerEmail}
                    onChange={this.handlePayerEmail}
                    style={{ base: { fontSize: "18px" } }}
                  />
                </div>

                <div className="AddCard-subheading">
                  {languageObj.subheading[2]}
                </div>

                <div className="AddCard-inputFieldContainer">
                  <input
                    className="AddCard-inputField"
                    country={"HK"}
                    value={this.state.payerInfo.payerPhone}
                    onChange={this.handlePayerPhone}
                    style={{ base: { fontSize: "18px" } }}
                  />
                </div>

                <div className="AddCard-subheading">
                  {languageObj.subheading[3]}
                </div>

                <div className="AddCard-inputFieldContainer">
                  <CardElement className="AddCard-inputField" />
                </div>

                <div className="AddCard-lockContainer">
                  <div className="AddCard-lock"></div>
                  <div className="AddCard-lockText">{languageObj.lock}</div>
                </div>

                <div className="AddCard-stripeContainer">
                  <div className="AddCard-stripe"></div>
                  <div className="AddCard-stripeText">
                    {languageObj.stripe[0]}
                    <br />
                    {languageObj.stripe[1]}
                  </div>
                </div>

                {renderErrorMsg(this.state.err)}

                <div
                  className={`AddCard-confirmBtn ${this.state.loading ? "AddCard-confirmBtn-disabled" : ""}`}
                  onClick={() => {
                    if (this.state.loading) return;
                    const isConfirm = window.confirm(
                      languageObj.confirmBtn + "?"
                    );
                    if (isConfirm) this._onClickBtn();
                  }}
                  style={buttonOverrideStyle}
                >
                  {languageObj.confirmBtn}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

AddCard.propTypes = {
  stripe: PropTypes.object.isRequired,
  language: PropTypes.string,
  languageObj: PropTypes.object,
  isAuth: PropTypes.bool,
  elements: PropTypes.object,
};

// Wrapper component to use Stripe hooks with class component
function AddCardWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <AddCard {...props} stripe={stripe} elements={elements} />;
}

export default AddCardWithStripe;
